import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as functions from '../functions'

import * as gql from './gql'
import type { TypeParams } from './types'

/**
 * Service Centers API service.
 */
@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link functions.Collection} */
  private readonly _functionsCollection = ngCore.inject(functions.Collection)

  /** @inheritdoc */
  public list = ec.models.Api.list.using.nothingButContext<TypeParams>(
    context => {
      if (!context) {
        return rxjs.of([])
      }

      return gql.queries.serviceCenters.executor({
        ...context,
      })
    },
  )

  /** @inheritdoc */
  public create: ec.models.Api<TypeParams>['create'] = (context, input) => {
    if (!context) {
      return rxjs.EMPTY
    }

    return gql.mutations.createServiceCenter.executor({
      ...context,
      input: gql.inputs.serviceCenter.INPUT.getGqlFrom(input),
    })
  }

  /** @inheritdoc */
  public update: ec.models.Api<TypeParams>['update'] = (
    context,
    { id, updates },
  ) => {
    if (!context) {
      return rxjs.EMPTY
    }

    return gql.mutations.updateServiceCenter.executor({
      ...context,
      id,
      input: gql.inputs.serviceCenter.UPDATES.getGqlFrom(updates),
    })
  }

  /** @inheritdoc */
  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)

    return gql.mutations.deleteServiceCenter.executor({
      ...context,
      id,
    })
  }

  /** @inheritdoc */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    rxjs.of([
      ec.models.Attribute.fromKey<TypeParams>('name', {
        name: 'Name',
      }),
      {
        getValueFrom: serviceCenter => {
          const spaceFunctions =
            this._functionsCollection.plugins.currentListPlugin.feature.selectors.entities.asSignal()

          return serviceCenter.functionalizations
            .map(({ functionId, percent }) => {
              const fn = spaceFunctions.find(f => f.id === functionId)
              const code = fn?.code ?? functionId
              return `${code}: ${Math.round(percent * 100)}%`
            })
            .join('; ')
        },
        id: 'functionalizations',
        name: 'Functionalizations',
      },
      {
        getValueFrom: serviceCenter => serviceCenter.assignedSpaceIds.length,
        id: 'assignedSpaceIds',
        name: 'Associated Records Count',
        valueInfo: {
          type: ents.core.common.value.types.Intrinsic.Integer,
        },
      },
    ])
}
